import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  UntypedFormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizes, ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import {
  RangeInputValue,
  RangeInputComponent,
} from '@app/shared/components/range-input/range-input.component';
import {
  Observable,
  Subject,
  Subscription,
  debounceTime,
  distinctUntilChanged,
  takeUntil,
} from 'rxjs';
import { NgIf, AsyncPipe } from '@angular/common';
import { TranslocoModule } from '@ngneat/transloco';

export const MAX_PRICE_AMOUNT = 6000;
const DEBOUNCE_USER_INPUT = 500;

@Component({
  selector: 'popsy-phone-price-filter',
  templateUrl: './phone-price-filter.template.html',
  styleUrls: ['./phone-price-filter.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule, RangeInputComponent, ReactiveFormsModule, NgIf, AsyncPipe],
})
export class PhonePriceFilterComponent implements OnChanges, OnDestroy {
  @Input() public loading?: boolean | null;
  @Input() public mobile?: boolean | null;
  @Input() public minPrice?: number | null;
  @Input() public maxPrice?: number | null;
  @Input() public currency?: string | null;

  @Output() public readonly priceRangeChange: EventEmitter<{
    minPrice: number;
    maxPrice: number;
  }>;
  @Output()
  public readonly debouncedPriceRangeChange: EventEmitter<{
    minPrice: number;
    maxPrice: number;
  }>;

  @ViewChild('minPrice') public minPriceElementRef?: ElementRef | null;
  @ViewChild('maxPrice') public maxPriceElementRef?: ElementRef | null;

  public selectedMinPrice: number | null = null;
  public selectedMaxPrice: number | null = null;
  public selectedCurrency: string | null = null;

  public priceFilterForm: UntypedFormGroup;
  public minPriceFormControl: UntypedFormControl;
  public maxPriceFormControl: UntypedFormControl;

  public readonly isRtlLayout$: Observable<boolean>;
  public readonly screenSize$: Observable<ScreenSizes>;
  public readonly lessThan$: Observable<ScreenSizeInfoState>;

  private readonly debouncePriceRangeSubject: Subject<{
    minPrice: number;
    maxPrice: number;
  }>;

  private readonly destroySubject: Subject<void>;

  private readonly subscriptions: Subscription;

  constructor(
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly changeDetector: ChangeDetectorRef
  ) {
    this.destroySubject = new Subject();
    this.priceRangeChange = new EventEmitter();
    this.debouncedPriceRangeChange = new EventEmitter();
    this.subscriptions = new Subscription();

    this.screenSize$ = this.akitaScreenQuery.selectSize();
    this.lessThan$ = this.akitaScreenQuery.selectLessThan();
    this.isRtlLayout$ = this.akitaRouterQuery.selectIsRtlLayout();

    this.minPriceFormControl = new UntypedFormControl('', {
      validators: Validators.compose([]),
      updateOn: 'blur',
    });

    this.maxPriceFormControl = new UntypedFormControl('', {
      validators: Validators.compose([]),
      updateOn: 'blur',
    });

    this.priceFilterForm = this.formBuilder.group({
      minPrice: this.minPriceFormControl,
      maxPrice: this.maxPriceFormControl,
    });

    this.minPriceFormControl.setValue(this.minPrice || 0);
    this.maxPriceFormControl.setValue(this.maxPrice || 0);

    this.selectedMinPrice = this.minPriceFormControl.value || 0;
    this.selectedMaxPrice = this.maxPriceFormControl.value || 0;

    if ((this.minPriceFormControl.value || 0) <= 0) {
      this.selectedMinPrice = 0;
    }
    if ((this.maxPriceFormControl.value || 0) >= MAX_PRICE_AMOUNT) {
      this.selectedMaxPrice = MAX_PRICE_AMOUNT;
    }

    this.subscriptions.add(
      this.minPriceFormControl.valueChanges
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: this.minPriceChanged.bind(this),
        })
    );

    this.subscriptions.add(
      this.maxPriceFormControl.valueChanges
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: this.maxPriceChanged.bind(this),
        })
    );

    this.debouncePriceRangeSubject = new Subject();
    this.subscriptions.add(
      this.debouncePriceRangeSubject
        .asObservable()
        .pipe(
          debounceTime(DEBOUNCE_USER_INPUT),
          distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
          takeUntil(this.destroySubject)
        )
        .subscribe({
          next: (value: { minPrice: number; maxPrice: number }) => {
            this.debouncedPriceRangeChange.next(value);
          },
        })
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.currency) {
      this.selectedCurrency = this.currency || null;
    }

    if (changes?.minPrice) {
      this.minPriceFormControl.setValue(this.minPrice || 0);

      this.selectedMinPrice = this.minPriceFormControl.value || 0;
      if ((this.minPriceFormControl.value || 0) <= 0) {
        this.selectedMinPrice = 0;
      }
    }

    if (changes?.maxPrice) {
      this.maxPriceFormControl.setValue(this.maxPrice || MAX_PRICE_AMOUNT);

      this.selectedMaxPrice = this.maxPriceFormControl.value || 0;
      if ((this.maxPriceFormControl.value || 0) >= MAX_PRICE_AMOUNT) {
        this.selectedMaxPrice = MAX_PRICE_AMOUNT;
      }
    }
  }

  public priceUpdated($event: RangeInputValue): void {
    this.minPriceFormControl.setValue($event.min.amount);
    this.maxPriceFormControl.setValue($event.max.amount);
    this.selectedMinPrice = this.minPriceFormControl.value || 0;
    this.selectedMaxPrice = this.maxPriceFormControl.value || 0;

    const priceRange = {
      minPrice: this.selectedMinPrice || 0,
      maxPrice: this.selectedMaxPrice || 0,
    };
    this.priceRangeChange.emit(priceRange);
    this.debouncePriceRangeSubject.next(priceRange);
  }

  private minPriceChanged(rawValue?: number | null): void {
    const value = Number(rawValue);
    if (value === value) {
      this.selectedMinPrice = value;
    } else {
      this.selectedMinPrice = this.minPriceFormControl.value || 0;
    }

    if ((this.minPriceFormControl.value || 0) <= 0) {
      this.selectedMinPrice = 0;
    }

    const priceRange = {
      minPrice: this.selectedMinPrice || 0,
      maxPrice: this.selectedMaxPrice || 0,
    };
    this.priceRangeChange.emit(priceRange);
    this.debouncePriceRangeSubject.next(priceRange);

    this.changeDetector.markForCheck();
  }

  private maxPriceChanged(rawValue?: number | null): void {
    const value = Number(rawValue);
    if (value === value && (this.selectedMinPrice || 0) < value) {
      this.selectedMaxPrice = value;
    } else {
      this.selectedMaxPrice = this.maxPriceFormControl.value || 0;
    }

    if ((this.maxPriceFormControl.value || 0) >= MAX_PRICE_AMOUNT) {
      this.selectedMaxPrice = MAX_PRICE_AMOUNT;
    }

    const priceRange = {
      minPrice: this.selectedMinPrice || 0,
      maxPrice: this.selectedMaxPrice || 0,
    };
    this.priceRangeChange.emit(priceRange);
    this.debouncePriceRangeSubject.next(priceRange);

    this.changeDetector.markForCheck();
  }

  public applyPriceFilter(name?: string | null): boolean {
    if (name === 'min-price' && this.minPriceElementRef?.nativeElement?.blur) {
      this.minPriceElementRef.nativeElement.blur();
    }

    if (name === 'max-price' && this.maxPriceElementRef?.nativeElement?.blur) {
      this.maxPriceElementRef.nativeElement.blur();
    }

    return false;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
